import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { KPICard } from '../components/KPICard';
import { ClientCard } from '../components/ClientCard';
import { TrafficLight } from '../components/TrafficLight';
import { ForecastLoader, DataLoader } from '../components/ForecastLoader';
import { getClients, getClient, getClientForecast, getClientPipeline, getExecutives, getRoutes } from '../lib/api';
import { formatNumber, formatPercent, formatMonth, chartColors } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { 
  LayoutGrid, 
  List, 
  Search, 
  Filter,
  ArrowLeft,
  TrendingUp,
  PieChart,
  Calendar,
  User,
  MapPin,
  Mail,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Map,
  Route
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

// Lazy load the map component
const ClientsMap = lazy(() => import('../components/ClientsMap').then(m => ({ default: m.ClientsMap })));

// List View Component
const ClientListView = ({ clients, onClientClick }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Cliente</th>
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Estado</th>
            <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Volumen</th>
            <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Share</th>
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Ruta</th>
            <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Ejecutivo</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {clients.map((client) => (
            <tr 
              key={client.id} 
              className="hover:bg-slate-50 cursor-pointer transition-colors"
              onClick={() => onClientClick(client.id)}
              data-testid={`client-row-${client.id}`}
            >
              <td className="px-4 py-3">
                <div>
                  <p className="font-medium text-slate-900">{client.name}</p>
                  <p className="text-xs text-slate-500">{client.industry}</p>
                </div>
              </td>
              <td className="px-4 py-3">
                <TrafficLight status={client.status} showLabel size="md" />
              </td>
              <td className="px-4 py-3 text-right">
                <span className="font-semibold text-slate-900">
                  {formatNumber(client.history?.[client.history.length - 1]?.kgs || 0)}
                </span>
                <span className="text-xs text-slate-500 ml-1">kgs</span>
              </td>
              <td className="px-4 py-3 text-right">
                <span className="font-medium text-slate-900">{formatPercent(client.share_of_wallet, 0)}</span>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm text-slate-600">{client.route}</span>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm text-slate-600">{client.executive_name}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Client Detail Page
const ClientDetailPage = ({ clientId }) => {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientRes, forecastRes, pipelineRes] = await Promise.all([
          getClient(clientId),
          getClientForecast(clientId),
          getClientPipeline(clientId)
        ]);
        setClient(clientRes.data);
        setForecast(forecastRes.data);
        setPipeline(pipelineRes.data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [clientId]);

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Detalle del Cliente</h1>
            <p className="text-slate-500 mt-1">Cargando información...</p>
          </div>
          <ForecastLoader type="forecast" />
        </div>
      </Layout>
    );
  }

  if (!client) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-500">Cliente no encontrado</p>
        </div>
      </Layout>
    );
  }

  // Prepare chart data
  const chartData = forecast?.data?.map(d => ({
    month: formatMonth(d.month),
    actual: d.actual,
    forecast: d.forecast,
    lower_80: d.lower_80,
    upper_80: d.upper_80,
  })) || [];

  // Find where forecast starts
  const forecastStartIndex = chartData.findIndex(d => d.forecast && !d.actual);

  return (
    <Layout>
      <div className="space-y-8" data-testid="client-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/clientes')}
            data-testid="back-to-clients-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <TrafficLight status={client.status} size="lg" />
              <h1 className="text-3xl font-bold text-slate-900 tracking-tight">{client.name}</h1>
            </div>
            <p className="text-slate-500 mt-1">{client.industry}</p>
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
              <User className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Ejecutivo</p>
              <p className="font-medium text-slate-900">{client.executive_name}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Ruta Principal</p>
              <p className="font-medium text-slate-900">{client.route}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
              <Mail className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Contacto</p>
              <p className="font-medium text-slate-900 truncate">{client.contact_name}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Forecast 6M</p>
              <p className="font-medium text-slate-900">{formatNumber(forecast?.total_forecast_6m)} kgs</p>
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Chart */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Histórico y Forecast</h2>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={chartColors.primary} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={chartColors.primary} stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorForecast" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={chartColors.secondary} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={chartColors.secondary} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis 
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => formatNumber(v)}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                    }}
                    formatter={(value) => [formatNumber(value) + ' kgs', '']}
                  />
                  {forecastStartIndex > 0 && (
                    <ReferenceLine 
                      x={chartData[forecastStartIndex - 1]?.month} 
                      stroke="#94a3b8" 
                      strokeDasharray="5 5"
                      label={{ value: 'Forecast', position: 'top', fill: '#94a3b8', fontSize: 11 }}
                    />
                  )}
                  <Area
                    type="monotone"
                    dataKey="upper_80"
                    stackId="1"
                    stroke="transparent"
                    fill={chartColors.secondary}
                    fillOpacity={0.1}
                  />
                  <Area
                    type="monotone"
                    dataKey="lower_80"
                    stackId="2"
                    stroke="transparent"
                    fill="#fff"
                  />
                  <Area
                    type="monotone"
                    dataKey="actual"
                    stroke={chartColors.primary}
                    strokeWidth={2}
                    fill="url(#colorActual)"
                    dot={{ fill: chartColors.primary, strokeWidth: 0, r: 3 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="forecast"
                    stroke={chartColors.secondary}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    fill="url(#colorForecast)"
                    dot={{ fill: chartColors.secondary, strokeWidth: 0, r: 3 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-6 mt-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: chartColors.primary }} />
                <span className="text-slate-600">Histórico</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: chartColors.secondary }} />
                <span className="text-slate-600">Forecast</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-3 rounded bg-emerald-100" />
                <span className="text-slate-600">IC 80%</span>
              </div>
            </div>
          </div>

          {/* Side Panel */}
          <div className="space-y-6">
            {/* Share of Wallet */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <PieChart className="w-4 h-4 text-slate-400" />
                Share of Wallet
              </h3>
              <div className="text-center mb-4">
                <p className="text-4xl font-bold text-slate-900">{formatPercent(client.share_of_wallet, 0)}</p>
                <p className="text-sm text-slate-500 mt-1">de su volumen total</p>
              </div>
              <div className="relative h-4 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="absolute h-full bg-indigo-500 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(client.share_of_wallet, 100)}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-xs text-slate-500">
                <span>Nuestro: {formatNumber(client.history?.[client.history.length - 1]?.kgs || 0)} kgs</span>
                <span>Total: {formatNumber(client.penta_total)} kgs</span>
              </div>
            </div>

            {/* Pipeline */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-slate-400" />
                Pipeline Activo
              </h3>
              {pipeline.length > 0 ? (
                <div className="space-y-3">
                  {pipeline.filter(p => p.status === 'open').map((opp) => (
                    <div key={opp.id} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex justify-between items-start mb-1">
                        <p className="text-sm font-medium text-slate-900">{formatNumber(opp.potential_kgs)} kgs</p>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          opp.probability >= 70 ? 'bg-emerald-100 text-emerald-700' :
                          opp.probability >= 40 ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {opp.probability}%
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">{opp.description}</p>
                      <p className="text-xs text-slate-400 mt-1">Cierre: {formatMonth(opp.estimated_close_month)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">Sin oportunidades activas</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

// Main Clients Page
const ClientesPage = () => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [clients, setClients] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // Default to list view
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    executive_id: '',
    route: ''
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientsRes, execsRes, routesRes] = await Promise.all([
          getClients(),
          getExecutives(),
          getRoutes()
        ]);
        setClients(clientsRes.data);
        setExecutives(execsRes.data);
        setRoutes(routesRes.data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters, itemsPerPage]);

  // If viewing a specific client
  if (clientId) {
    return <ClientDetailPage clientId={clientId} />;
  }

  // Filter clients
  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.industry.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filters.status || filters.status === 'all' || client.status === filters.status;
    const matchesExec = !filters.executive_id || filters.executive_id === 'all' || client.executive_id === filters.executive_id;
    const matchesRoute = !filters.route || filters.route === 'all' || client.route === filters.route;
    return matchesSearch && matchesStatus && matchesExec && matchesRoute;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedClients = filteredClients.slice(startIndex, endIndex);

  // Stats
  const stats = {
    total: clients.length,
    green: clients.filter(c => c.status === 'green').length,
    yellow: clients.filter(c => c.status === 'yellow').length,
    red: clients.filter(c => c.status === 'red').length,
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Clientes</h1>
            <p className="text-slate-500 mt-1">Cargando cartera de clientes...</p>
          </div>
          <DataLoader type="clients" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="clientes-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Clientes</h1>
            <p className="text-slate-500 mt-1">{clients.length} clientes en cartera</p>
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'list' ? 'bg-white shadow text-slate-900' : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="view-list-btn"
            >
              <List className="w-4 h-4" />
              Lista
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'cards' ? 'bg-white shadow text-slate-900' : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="view-cards-btn"
            >
              <LayoutGrid className="w-4 h-4" />
              Cards
            </button>
            <button
              onClick={() => setViewMode('map-clients')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'map-clients' ? 'bg-white shadow text-slate-900' : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="view-map-clients-btn"
            >
              <Map className="w-4 h-4" />
              Mapa
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              <p className="text-xs text-slate-500">Total</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-emerald-600">{stats.green}</p>
              <p className="text-xs text-slate-500">Creciendo</p>
            </div>
            <TrafficLight status="green" size="lg" />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-amber-500">{stats.yellow}</p>
              <p className="text-xs text-slate-500">Potencial</p>
            </div>
            <TrafficLight status="yellow" size="lg" />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-rose-500">{stats.red}</p>
              <p className="text-xs text-slate-500">En riesgo</p>
            </div>
            <TrafficLight status="red" size="lg" />
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="search-clients-input"
            />
          </div>
          
          <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v})}>
            <SelectTrigger className="w-[140px]" data-testid="filter-status-select">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="green">Creciendo</SelectItem>
              <SelectItem value="yellow">Potencial</SelectItem>
              <SelectItem value="red">En riesgo</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.executive_id} onValueChange={(v) => setFilters({...filters, executive_id: v})}>
            <SelectTrigger className="w-[180px]" data-testid="filter-executive-select">
              <SelectValue placeholder="Ejecutivo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {executives.map(exec => (
                <SelectItem key={exec.id} value={exec.id}>{exec.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.route} onValueChange={(v) => setFilters({...filters, route: v})}>
            <SelectTrigger className="w-[140px]" data-testid="filter-route-select">
              <SelectValue placeholder="Ruta" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {routes.map(route => (
                <SelectItem key={route} value={route}>{route}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Results */}
        {filteredClients.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Filter className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No se encontraron clientes con los filtros aplicados</p>
          </div>
        ) : viewMode === 'map-clients' || viewMode === 'map-routes' ? (
          <Suspense fallback={
            <div className="h-[500px] rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center">
              <div className="animate-spin w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full" />
            </div>
          }>
            <ClientsMap 
              clients={filteredClients} 
              onClientClick={(id) => navigate(`/clientes/${id}`)}
              viewMode={viewMode === 'map-routes' ? 'routes' : 'clients'}
            />
          </Suspense>
        ) : viewMode === 'cards' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {paginatedClients.map(client => (
              <ClientCard key={client.id} client={client} />
            ))}
          </div>
        ) : (
          <ClientListView clients={paginatedClients} onClientClick={(id) => navigate(`/clientes/${id}`)} />
        )}

        {/* Pagination - only for list/cards view */}
        {filteredClients.length > 0 && (viewMode === 'list' || viewMode === 'cards') && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <span>Mostrando</span>
              <Select value={itemsPerPage.toString()} onValueChange={(v) => setItemsPerPage(parseInt(v))}>
                <SelectTrigger className="w-[70px] h-8" data-testid="items-per-page-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="15">15</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
              <span>de {filteredClients.length} clientes</span>
            </div>
            
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                data-testid="pagination-first"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                data-testid="pagination-prev"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              <div className="flex items-center gap-1 mx-2">
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setCurrentPage(pageNum)}
                      data-testid={`pagination-page-${pageNum}`}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                data-testid="pagination-next"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                data-testid="pagination-last"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="text-sm text-slate-500">
              Página {currentPage} de {totalPages}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ClientesPage;
